import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const NotificationContext = createContext(null);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectFnRef = useRef(null);

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const WS_URL = API_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

    // Disconnect from WebSocket
    const disconnect = useCallback(() => {
        if (wsRef.current) {
            wsRef.current.close(1000, 'User logged out');
            wsRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
        }
        setIsConnected(false);
    }, []);

    // Connect to WebSocket
    const connect = useCallback(() => {
        if (!isAuthenticated || !user?.user_id) return;

        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const ws = new WebSocket(`${WS_URL}/ws/${user.user_id}`);

            ws.onopen = () => {
                // Send auth token as first message (token must not travel in URL)
                ws.send(JSON.stringify({ type: 'auth', token }));
                setIsConnected(true);
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                }
            };

            ws.onmessage = (event) => {
                try {
                    const notification = JSON.parse(event.data);
                    
                    if (notification === 'pong') return;

                    setNotifications(prev => [notification, ...prev].slice(0, 50));
                    setUnreadCount(prev => prev + 1);

                    const toastOptions = { duration: 5000 };
                    
                    switch (notification.type) {
                        case 'new_application':
                            toast.success(notification.title, {
                                ...toastOptions,
                                description: notification.message
                            });
                            break;
                        case 'application_status':
                            if (notification.data?.status === 'shortlisted' || notification.data?.status === 'hired') {
                                toast.success(notification.title, {
                                    ...toastOptions,
                                    description: notification.message
                                });
                            } else {
                                toast.info(notification.title, {
                                    ...toastOptions,
                                    description: notification.message
                                });
                            }
                            break;
                        case 'certificate_ready':
                            toast.success(notification.title, {
                                ...toastOptions,
                                description: notification.message,
                                action: {
                                    label: 'Download',
                                    onClick: () => window.open(notification.action_url, '_blank')
                                }
                            });
                            break;
                        case 'job_alert':
                            toast.info(notification.title, {
                                ...toastOptions,
                                description: notification.message
                            });
                            break;
                        default:
                            toast(notification.title, {
                                ...toastOptions,
                                description: notification.message
                            });
                    }
                } catch (e) {
                    console.error('Failed to parse WebSocket message:', e);
                }
            };

            ws.onclose = (event) => {
                setIsConnected(false);
                wsRef.current = null;

                if (event.code !== 1000 && isAuthenticated && reconnectFnRef.current) {
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectFnRef.current();
                    }, 5000);
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

            wsRef.current = ws;

            const pingInterval = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send('ping');
                }
            }, 30000);

            return () => {
                clearInterval(pingInterval);
            };
        } catch (e) {
            console.error('Failed to connect WebSocket:', e);
        }
    }, [isAuthenticated, user?.user_id, WS_URL]);

    // Store connect function in ref for reconnection
    useEffect(() => {
        reconnectFnRef.current = connect;
    }, [connect]);

    // Connect when authenticated
    useEffect(() => {
        if (isAuthenticated && user?.user_id) {
            connect();
        } else {
            disconnect();
        }

        return () => {
            disconnect();
        };
    }, [isAuthenticated, user?.user_id, connect, disconnect]);

    const markAsRead = useCallback((notificationId) => {
        setNotifications(prev =>
            prev.map(n =>
                n.id === notificationId ? { ...n, is_read: true } : n
            )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    }, []);

    const markAllAsRead = useCallback(() => {
        setNotifications(prev =>
            prev.map(n => ({ ...n, is_read: true }))
        );
        setUnreadCount(0);
    }, []);

    const clearAll = useCallback(() => {
        setNotifications([]);
        setUnreadCount(0);
    }, []);

    const value = {
        notifications,
        unreadCount,
        isConnected,
        markAsRead,
        markAllAsRead,
        clearAll,
        connect,
        disconnect
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationContext;
